import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';
import { OrganizationGroupCodesDto, RoleEnumDto } from '.generated/api';
import OrganizationGroupCodeSelect from 'features/organizations/OrganizationGroupCodeSelect';
import PrepareOrganizationDialog from './PrepareOrganizationDialog';

const OrganizationCard: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prepareDialogOpen, setPrepareDialogOpen] = useState(false);
  const [input, setInput] = useState({
    name: '',
    groupCode: '',
    preferLinksInEmail: false,
  });
  const [isMounted, setIsMounted] = useState(true);

  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );

  const isGlobalAdmin = useSelector(
    (state: ApplicationState) =>
      state.isLoggedIn.userAccess?.isGlobalAdmin ?? false,
  );

  const organizationRelations = useSelector(
    (state: ApplicationState) => state.isLoggedIn.userAccess?.organizations,
  );

  const canEditLinkPreference =
    isGlobalAdmin ||
    (!!organization &&
      !!organizationRelations?.some(
        (relation) =>
          relation.id === organization.organizationId &&
          relation.roles.includes(RoleEnumDto.ORGANIZATION_ADMIN),
      ));

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track organization
  useEffect(() => {
    resetInput();
  }, [organization]);

  const resetInput = () => {
    if (organization) {
      setInput({
        name: organization.name,
        groupCode: organization.groupCode ?? '',
        preferLinksInEmail: organization.preferLinksInEmail ?? false,
      });
    } else {
      setInput({
        name: '',
        groupCode: '',
        preferLinksInEmail: false,
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput((input) => ({ ...input, [name]: value }));
  };

  const handlePreferLinksInEmailChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setInput((input) => ({
      ...input,
      preferLinksInEmail: e.target.checked,
    }));
  };

  const handleSave = () => {
    if (organization) {
      setIsLoading(true);
      dispatch(
        organizationActions.updateOrganization(
          organization.organizationId,
          input.name,
          input.groupCode as OrganizationGroupCodesDto,
          input.preferLinksInEmail,
          (error) => {
            if (isMounted) {
              setIsLoading(false);
              setEditMode(!!error);
            }
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            }
          },
        ),
      );
    }
  };

  const handleEdit = () => {
    resetInput();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleOpenPrepareDialog = () => {
    setPrepareDialogOpen(true);
  };

  const handleClosePrepareDialog = () => {
    setPrepareDialogOpen(false);
  };

  const hasGroupCode = !!organization?.groupCode;

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      isValid={input.name.length > 0}
      title={t(
        'organization_page.organization_information.labels.organization',
      )}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.organization_name',
              )}
              name="name"
              value={input.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <OrganizationGroupCodeSelect
              onChange={handleChange}
              value={input?.groupCode ?? ''}
              disabled={!isGlobalAdmin}
              helpText={
                !isGlobalAdmin
                  ? t(
                      'organization_page.organization_information.labels.group_code_super_admin_only',
                    )
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={input.preferLinksInEmail}
                  onChange={handlePreferLinksInEmailChange}
                  disabled={!canEditLinkPreference}
                />
              }
              label={
                <>
                  {t(
                    'organization_page.organization_information.labels.prefer_links_in_email',
                  )}
                  <Tooltip
                    title={t(
                      'organization_page.organization_information.labels.prefer_links_in_email_description',
                    )}
                  >
                    <InfoOutlinedIcon
                      aria-label={String(
                        t(
                          'organization_page.organization_information.labels.prefer_links_in_email_description',
                        ),
                      )}
                      fontSize="small"
                      style={{ marginLeft: '0.25rem', verticalAlign: 'middle' }}
                    />
                  </Tooltip>
                </>
              }
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Typography variant="body1">
            <b>
              {t(
                'organization_page.organization_information.labels.organization_name',
              )}
              :&nbsp;
            </b>
            {organization && (organization.name ?? '')}
          </Typography>
          <Typography variant="body1">
            <b>
              {t(
                'organization_page.organization_information.labels.organization_group_code',
              )}
              :&nbsp;
            </b>
            {organization?.groupCode ?? ''}
          </Typography>
          <Typography variant="body1">
            <b>
              {t(
                'organization_page.organization_information.labels.prefer_links_in_email',
              )}
              :&nbsp;
            </b>
            {organization?.preferLinksInEmail
              ? t('common.yes')
              : t('common.no')}
            <Tooltip
              title={t(
                'organization_page.organization_information.labels.prefer_links_in_email_description',
              )}
            >
              <InfoOutlinedIcon
                aria-label={String(
                  t(
                    'organization_page.organization_information.labels.prefer_links_in_email_description',
                  ),
                )}
                fontSize="small"
                style={{ marginLeft: '0.25rem', verticalAlign: 'middle' }}
              />
            </Tooltip>
          </Typography>
          <Button
            variant="contained"
            color="primary"
            disabled={!hasGroupCode}
            onClick={handleOpenPrepareDialog}
            style={{ marginTop: '1rem' }}
          >
            {t('organization_page.prepare_forest.button')}
          </Button>
          <PrepareOrganizationDialog
            isOpen={prepareDialogOpen}
            handleClose={handleClosePrepareDialog}
            organizationId={organization?.organizationId ?? ''}
            regionCode={
              organization?.forestDistrict?.regionCode?.toString() ?? ''
            }
            inspectorateCode={
              organization?.forestDistrict?.inspectorateCode?.toString() ?? ''
            }
          />
        </>
      )}
    </EditCard>
  );
};

export default OrganizationCard;
